import {PostSignupOfferCallout} from "./PostSignupOfferCallout";
import {POST_SIGNUP_CALLOUT_MOUNTED} from "./postSignupCalloutPlacement";
import {TrialExpiryOfferCallout} from "./TrialExpiryOfferCallout";
import {ReferralGiftCallout} from "referral/ReferralGiftCallout";
import type {ReferralOfferState} from "referral/useReferralOffer";
import type {BesideOfferPlacement} from "./besideOfferHost";

/**
 * The panel's subscription-offer callouts, behind ONE gate (kitecorp/transferify-ui#1597).
 *
 * <b>Why a component and not two conditionals in the panel.</b> ui#1454's constraint — "NO subscription
 * pitch competing at this checkout. Single CTA, clean." — was enforced at the component (`OverLimitNotice`
 * carries one CTA and no upgrade vocabulary, sweep-proven) and broken at the COMPOSITION: the panel
 * rendered a pitch callout at the top and the over-limit checkout below it, to the same reader, and no
 * guard could see it because each read one file. One mounting point for every pitch is what makes the
 * rule enforceable: `scripts/over-limit-single-cta-sweep.mjs` pins that the panel renders pitches ONLY
 * through here, and that `suppressed` is a real expression over the over-limit resolution.
 *
 * The two callouts remain structurally exclusive with each other (free plan vs live trial); this gate
 * is about their relationship to the CHECKOUT, not to each other.
 *
 * <b>The post-signup callout is currently OFF</b> (kitecorp/transferify-ui#1653, owner 2026-08-29):
 * the full gold offer moved to the transfer-success card, and nothing pitches before the upload any
 * more. It is switched off here rather than deleted — see {@link POST_SIGNUP_CALLOUT_MOUNTED} for the
 * decision and for what restoring it costs.
 *
 * <b>The referred friend's gift joins them</b> (kitecorp/transferify-ui#1993) rather than mounting
 * beside them in the panel, and for exactly the reason above: it is a subscription pitch, so the
 * over-limit checkout must suppress it too. Its state arrives as a PROP because the panel owns the one
 * picker every one of its surfaces opens — the strip, the download-cap wall and the storage notice all
 * reach the same dialog, which a second `useReferralOffer` here would fork in two.
 *
 * @param suppressed the over-limit checkout is on screen (`overLimitCheckoutOnScreen`), so no pitch
 *                   may compete with it
 * <b>The trial-expiry offer is still mounted here</b> (ui#2949) though it no longer paints in the
 * panel: it portals beside the card, into `beside`. Mounting it here is what keeps it behind this gate.
 *
 * @param referral   the panel's referral picker state, or undefined where no panel owns one
 * @param beside     where the trial-expiry offer hangs beside the card
 */
export function OfferCallouts({suppressed, referral, beside}: {suppressed: boolean; referral?: ReferralOfferState; beside: BesideOfferPlacement}) {
	if (suppressed) {
		return null;
	}
	return (
		<>
			{POST_SIGNUP_CALLOUT_MOUNTED && <PostSignupOfferCallout/>}
			<TrialExpiryOfferCallout beside={beside}/>
			{referral !== undefined && <ReferralGiftCallout offer={referral}/>}
		</>
	);
}
